/**
 * API clients for the two new homepage endpoints.
 *
 * GET /api/v1/schools/count
 *   Returns total school count + per-division breakdown for the homepage division tiles.
 *
 * GET /api/v1/schools/distribution
 *   Returns a 5-bucket score histogram for the homepage live preview pane.
 *
 * Both endpoints are public (no auth required).
 */

// ── /count ─────────────────────────────────────────────────────────────────

export interface DivisionCount {
  division: string;
  label: string;
  count: number;
  note: string;
}

export interface SchoolCountResponse {
  total: number;
  by_division: DivisionCount[];
}

/**
 * Fetch school counts grouped by division.
 * Used by the "Or browse by division" section on the homepage.
 */
export async function fetchSchoolCount(
  signal?: AbortSignal,
): Promise<SchoolCountResponse> {
  const response = await fetch('/api/v1/schools/count', signal ? { signal } : undefined);
  if (!response.ok) {
    throw new Error(`School count fetch failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

// ── /distribution ──────────────────────────────────────────────────────────

export interface DistributionBucket {
  /** e.g. "0–20", "20–40", "40–60", "60–80", "80+" */
  range: string;
  count: number;
  /** "low" | "med" | "high" */
  tier: 'low' | 'med' | 'high';
}

export interface DistributionFilter {
  sport: string;
  position: string;
  division?: string;
  target_year?: number;
}

/**
 * Fetch the score distribution histogram for the given filter.
 * The live preview pane uses this to render the ScoreHistogram.
 * sport and position are both required by the backend.
 */
export async function fetchScoreDistribution(
  filter: DistributionFilter,
  signal?: AbortSignal,
): Promise<DistributionBucket[]> {
  const params = new URLSearchParams();
  params.set('sport', filter.sport);
  params.set('position', filter.position);
  if (filter.division) params.set('division', filter.division);
  if (filter.target_year) params.set('target_year', String(filter.target_year));

  const response = await fetch(
    `/api/v1/schools/distribution?${params.toString()}`,
    signal ? { signal } : undefined,
  );
  if (!response.ok) {
    throw new Error(
      `Score distribution fetch failed: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}
